export type VoiceAssessmentScoreTimeframe = 'weekly' | 'monthly' | 'sixMonths'

export type VoiceAssessmentChartXLabel =
  | string
  | { line1: string; line2: string }

export interface VoiceAssessmentScorePoint {
  x: number
  score: number
  displayScore: number
  created: Date
  segmentId: string
}

export interface VoiceAssessmentScoreSeries {
  points: VoiceAssessmentScorePoint[]
  xLabels: VoiceAssessmentChartXLabel[]
  visibleLabelCount: number
  columnWidthMultiplier: number
}

interface VoiceAssessmentScoreSession {
  created: Date
  score?: number | null
}

const DAY_MS = 24 * 60 * 60 * 1000
const X_AXIS_LABEL_COUNT = 7
const MONTHLY_VISIBLE_DAY_COUNT = 31
const DEFAULT_COLUMN_WIDTH_MULTIPLIER = 1

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function startOfLocalMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date)
  const targetDate = next.getDate()
  next.setDate(1)
  next.setMonth(next.getMonth() + months)
  const lastDateOfTargetMonth = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0,
  ).getDate()
  next.setDate(Math.min(targetDate, lastDateOfTargetMonth))
  return next
}

function earliestStartDate({
  fallbackStart,
  scoredSessions,
}: {
  fallbackStart: Date
  scoredSessions: readonly VoiceAssessmentScoreSession[]
}): Date {
  const earliestScoredDay = scoredSessions[0]?.created
  if (earliestScoredDay == null) {
    return fallbackStart
  }

  const earliest = startOfLocalDay(earliestScoredDay)
  return earliest.getTime() < fallbackStart.getTime() ? earliest : fallbackStart
}

function calendarDayDiff(start: Date, end: Date): number {
  return Math.round(
    (startOfLocalDay(end).getTime() - startOfLocalDay(start).getTime()) /
      DAY_MS,
  )
}

function localDayKey(date: Date): string {
  const day = startOfLocalDay(date)
  const year = day.getFullYear()
  const month = String(day.getMonth() + 1).padStart(2, '0')
  const dateOfMonth = String(day.getDate()).padStart(2, '0')
  return `${year}-${month}-${dateOfMonth}`
}

function buildWeeklyLabels(
  start: Date,
  totalDays: number,
  locale: string,
): VoiceAssessmentChartXLabel[] {
  return Array.from({ length: totalDays }, (_, index) => {
    const day = addDays(start, index)
    return {
      line1: day.toLocaleDateString(locale, { weekday: 'short' }),
      line2: day.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
      }),
    }
  })
}

function buildMonthLabels(
  start: Date,
  totalDays: number,
  locale: string,
): VoiceAssessmentChartXLabel[] {
  return Array.from({ length: totalDays }, (_, index) => {
    const daysFromEnd = totalDays - 1 - index
    if (daysFromEnd % 7 !== 0) {
      return { line1: '', line2: '' }
    }

    const day = addDays(start, index)
    const month = day
      .toLocaleDateString(locale, {
        month: 'short',
      })
      .replace(/\.$/, '')
      .toLocaleLowerCase(locale)
    return {
      line1: String(day.getDate()),
      line2: month,
    }
  })
}

function buildSixMonthLabels(
  start: Date,
  totalDays: number,
  locale: string,
): VoiceAssessmentChartXLabel[] {
  const firstMonth = startOfLocalMonth(start)
  const end = addDays(start, totalDays - 1)
  const monthCount =
    (end.getFullYear() - firstMonth.getFullYear()) * 12 +
    (end.getMonth() - firstMonth.getMonth()) +
    1

  return Array.from({ length: monthCount }, (_, index) =>
    addMonths(firstMonth, index).toLocaleDateString(locale, {
      month: 'short',
    }),
  )
}

function visibleLabelCountForTimeframe(
  timeframe: VoiceAssessmentScoreTimeframe,
): number {
  if (timeframe === 'weekly') {
    return 7
  }
  if (timeframe === 'monthly') {
    return MONTHLY_VISIBLE_DAY_COUNT
  }
  if (timeframe === 'sixMonths') {
    return 6
  }
  return X_AXIS_LABEL_COUNT
}

function columnWidthMultiplierForTimeframe(
  timeframe: VoiceAssessmentScoreTimeframe,
): number {
  return DEFAULT_COLUMN_WIDTH_MULTIPLIER
}

function dedupeLatestScoredSessionByDay(
  sessions: readonly VoiceAssessmentScoreSession[],
): VoiceAssessmentScoreSession[] {
  const latestByDay = new Map<string, VoiceAssessmentScoreSession>()

  sessions.forEach((session) => {
    if (session.score == null) {
      return
    }

    const key = localDayKey(session.created)
    const current = latestByDay.get(key)
    if (
      current == null ||
      session.created.getTime() > current.created.getTime()
    ) {
      latestByDay.set(key, session)
    }
  })

  return [...latestByDay.values()].sort(
    (a, b) => a.created.getTime() - b.created.getTime(),
  )
}

export function getLatestScoredVoiceAssessmentSession<
  T extends VoiceAssessmentScoreSession,
>(sessions: readonly T[]): T | null {
  return (
    sessions
      .filter((session) => session.score != null)
      .sort((a, b) => b.created.getTime() - a.created.getTime())[0] ?? null
  )
}

export function buildVoiceAssessmentScoreSeries({
  sessions,
  timeframe,
  locale,
  today = new Date(),
}: {
  sessions: readonly VoiceAssessmentScoreSession[]
  timeframe: VoiceAssessmentScoreTimeframe
  locale: string
  today?: Date
}): VoiceAssessmentScoreSeries {
  const end = startOfLocalDay(today)
  const scoredSessions = dedupeLatestScoredSessionByDay(sessions)

  const sixMonthsStart = addMonths(end, -6)
  const start = earliestStartDate({
    fallbackStart: sixMonthsStart,
    scoredSessions,
  })
  const totalDays = Math.max(1, calendarDayDiff(start, end) + 1)

  const sessionsInRange = scoredSessions.filter((session) => {
    const day = startOfLocalDay(session.created)
    return day.getTime() >= start.getTime() && day.getTime() <= end.getTime()
  })

  let currentSegment = 0
  let previousDayIndex: number | null = null
  const points = sessionsInRange.map((session) => {
    const dayIndex = calendarDayDiff(start, session.created)
    if (previousDayIndex != null && dayIndex !== previousDayIndex + 1) {
      currentSegment += 1
    }
    previousDayIndex = dayIndex

    return {
      x: totalDays === 1 ? 0.5 : dayIndex / (totalDays - 1),
      score: session.score ?? 0,
      displayScore: session.score ?? 0,
      created: session.created,
      segmentId: String(currentSegment),
    }
  })

  const xLabels =
    timeframe === 'weekly'
      ? buildWeeklyLabels(start, totalDays, locale)
      : timeframe === 'monthly'
        ? buildMonthLabels(start, totalDays, locale)
        : buildSixMonthLabels(start, totalDays, locale)

  return {
    points,
    xLabels,
    visibleLabelCount: visibleLabelCountForTimeframe(timeframe),
    columnWidthMultiplier: columnWidthMultiplierForTimeframe(timeframe),
  }
}
